import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { toBill, type Bill } from './recurringWire'

type UpdateBody = components['schemas']['RecurringUpdate']

/**
 * Why a recurring PATCH did not land. Carries the KIND only: each caller maps it
 * to its own sentence, because the edit form and the settings panel say
 * different things about the same 409 and the same 422.
 */
export class RecurringWriteError extends Error {
  readonly kind: 'conflict' | 'invalid' | 'removed' | 'rate_limited' | 'not_pending' | 'failed'
  constructor(kind: RecurringWriteError['kind'], cause?: unknown) {
    super(kind, { cause })
    this.name = 'RecurringWriteError'
    this.kind = kind
  }
}

/**
 * The kinds the BILL writes (`patchRecurring`, `createFromTransaction`) can
 * produce. `rate_limited` and `not_pending` belong to the suggestion endpoints
 * only, so a bill write's consumer narrows to this with `billKind`.
 */
export type BillWriteKind = Exclude<RecurringWriteError['kind'], 'rate_limited' | 'not_pending'>
export const billKind = (e: RecurringWriteError): BillWriteKind =>
  e.kind === 'rate_limited' || e.kind === 'not_pending' ? 'failed' : e.kind

function kindOf(status: number): RecurringWriteError['kind'] {
  if (status === 409) return 'conflict'
  if (status === 404) return 'removed'
  if (status === 422) return 'invalid'
  if (status === 429) return 'rate_limited'
  return 'failed'
}

/**
 * `PATCH /recurring/{id}`, once: the bill as the server now has it, or a
 * `RecurringWriteError`. Never retried — a 409 means the caller's `version` is
 * stale, and replaying it would overwrite what the other device wrote.
 */
export async function patchRecurring(id: string, body: UpdateBody): Promise<Bill> {
  let result
  try {
    result = await api.PATCH('/api/v1/recurring/{recurring_id}', { params: { path: { recurring_id: id } }, body })
  } catch (cause) {
    throw new RecurringWriteError('failed', cause)
  }
  const { data, error, response } = result
  if (error !== undefined || data === undefined) throw new RecurringWriteError(kindOf(response.status), error)
  return toBill(data)
}

type FromTransactionBody = components['schemas']['CreateFromTransactionRequest']

/**
 * `POST /recurring/from-transaction/{id}`, once. The server takes the amount,
 * merchant and account from the transaction itself, so the body is only what
 * the user chose. A 409 is "this transaction already belongs to a stream" and a
 * 404 is "it is gone" — `kindOf` maps them to `conflict` and `removed`, and the
 * caller words each for a transaction rather than a bill.
 */
export async function createFromTransaction(txnId: string, body: FromTransactionBody): Promise<Bill> {
  let result
  try {
    result = await api.POST('/api/v1/recurring/from-transaction/{transaction_id}', {
      params: { path: { transaction_id: txnId } },
      body,
    })
  } catch (cause) {
    throw new RecurringWriteError('failed', cause)
  }
  const { data, error, response } = result
  if (error !== undefined || data === undefined) throw new RecurringWriteError(kindOf(response.status), error)
  return toBill(data)
}

/**
 * A suggestion write's kind. A 400 is "no longer pending", which only the
 * suggestion endpoints mean by it, so it is read here and not in `kindOf`.
 */
const suggestionKind = (status: number): RecurringWriteError['kind'] => (status === 400 ? 'not_pending' : kindOf(status))

type AcceptSuggestionBody = components['schemas']['RecurringSuggestionAcceptRequest']

/**
 * `POST /recurring-suggestions/{id}/accept` with an empty body (S2: one tap, no
 * picker; the server picks its own defaults). Resolves to how many past
 * payments it linked. Once, never retried.
 */
export async function acceptSuggestion(id: string): Promise<number> {
  let result
  try {
    result = await api.POST('/api/v1/recurring-suggestions/{suggestion_id}/accept', {
      params: { path: { suggestion_id: id } },
      // `{}` is the contract; the generated type lists the server's defaults as required.
      body: {} as AcceptSuggestionBody,
    })
  } catch (cause) {
    throw new RecurringWriteError('failed', cause)
  }
  const { data, error, response } = result
  if (error !== undefined || data === undefined) throw new RecurringWriteError(suggestionKind(response.status), error)
  return data.backfill_count
}

/** `POST /recurring-suggestions/{id}/dismiss`. The server never suggests it again (S3). */
export async function dismissSuggestion(id: string): Promise<void> {
  let result
  try {
    result = await api.POST('/api/v1/recurring-suggestions/{suggestion_id}/dismiss', {
      params: { path: { suggestion_id: id } },
    })
  } catch (cause) {
    throw new RecurringWriteError('failed', cause)
  }
  const { error, response } = result
  if (error !== undefined || !response.ok) throw new RecurringWriteError(suggestionKind(response.status), error)
}

/** `POST /recurring-suggestions/detect`: how many suggestions it created. A 429 is `rate_limited` (S1). */
export async function detectSuggestions(): Promise<number> {
  let result
  try {
    result = await api.POST('/api/v1/recurring-suggestions/detect')
  } catch (cause) {
    throw new RecurringWriteError('failed', cause)
  }
  const { data, error, response } = result
  if (error !== undefined || data === undefined) throw new RecurringWriteError(suggestionKind(response.status), error)
  return data.suggestions_created
}
